/**
 * O piloto automático na tela: as rotas e os hooks (docs/PILOTO-AUTOMATICO.md §16).
 *
 * Toda chave começa por `piloto`: o stream invalida o prefixo inteiro a cada mudança de
 * conversa, e o contador da barra lateral acompanha sem polling próprio.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AutopilotMode,
  AutopilotSettings,
  AutopilotSummary,
  ConversationSummary,
  DoctorAutopilot,
  MemoryContent,
  SimulatorResult,
  SimulatorTurn,
  Uuid,
} from '@zenda/api-contract';
import { request } from './api';
import { ERRO_NA_TELA } from './aviso-de-erro';
import { keys } from './queries';

export const chavesDoPiloto = {
  resumo: ['piloto', 'resumo'] as const,
  config: ['piloto', 'config'] as const,
  aba: (clientId: Uuid) => ['piloto', 'aba', clientId] as const,
};

export function usePilotoResumo(ativo = true) {
  return useQuery<AutopilotSummary>({
    queryKey: chavesDoPiloto.resumo,
    queryFn: () => request<AutopilotSummary>('/api/piloto/resumo'),
    enabled: ativo,
    refetchInterval: 60_000,
  });
}

export function useMudarModo() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (mode: AutopilotMode) =>
      request<{ mode: AutopilotMode }>('/api/piloto/modo', { method: 'PUT', body: { mode } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['piloto'] });
      void client.invalidateQueries({ queryKey: keys.conversations });
    },
  });
}

/**
 * "Precisa de você" pelo servidor: a lista comum traz as 100 conversas mais recentes, e
 * uma escalada mais antiga sumiria do filtro enquanto o contador ainda a conta.
 */
export function usePrecisaDeVoce(ativo: boolean) {
  return useQuery<readonly ConversationSummary[]>({
    queryKey: [...keys.conversations, 'precisa'],
    queryFn: () => request<readonly ConversationSummary[]>('/api/conversations?status=needs_you'),
    enabled: ativo,
    refetchInterval: 60_000,
  });
}

/** "Corrigir": a versão da atendente vira proposta de memória (plano §6.3). */
export function useCorrecao(conversationId: Uuid) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (v: { messageId: Uuid; texto: string }) =>
      request<{ id: Uuid }>(`/api/conversations/${conversationId}/piloto/correcao`, {
        method: 'POST',
        body: v,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.conversation(conversationId) });
      void client.invalidateQueries({ queryKey: ['piloto'] });
    },
  });
}

export function usePilotoConfig() {
  return useQuery<AutopilotSettings>({
    queryKey: chavesDoPiloto.config,
    queryFn: () => request<AutopilotSettings>('/api/piloto/config'),
  });
}

export function useSalvarPilotoConfig() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<AutopilotSettings>) =>
      request<AutopilotSettings>('/api/piloto/config', { method: 'PUT', body }),
    meta: ERRO_NA_TELA,
    onSuccess: (dado) => {
      client.setQueryData(chavesDoPiloto.config, dado);
      void client.invalidateQueries({ queryKey: ['piloto'] });
    },
  });
}

export function useAbaDoPiloto(clientId: Uuid) {
  return useQuery<DoctorAutopilot>({
    queryKey: chavesDoPiloto.aba(clientId),
    queryFn: () => request<DoctorAutopilot>(`/api/clients/${clientId}/piloto`),
  });
}

/** As mutações da aba devolvem a aba inteira: a tela troca o cache sem buscar de novo. */
export function useAcoesDaAba(clientId: Uuid) {
  const client = useQueryClient();
  const base = `/api/clients/${clientId}/piloto`;
  const trocar = (dado: DoctorAutopilot) => {
    client.setQueryData(chavesDoPiloto.aba(clientId), dado);
    void client.invalidateQueries({ queryKey: chavesDoPiloto.resumo });
  };
  const aba = <V>(fn: (v: V) => Promise<DoctorAutopilot>) =>
    useMutation({ mutationFn: fn, meta: ERRO_NA_TELA, onSuccess: trocar });
  return {
    config: aba((body: Partial<AutopilotSettings>) =>
      request<DoctorAutopilot>(`${base}/config`, { method: 'PUT', body }),
    ),
    memoria: aba((content: MemoryContent) =>
      request<DoctorAutopilot>(`${base}/memoria`, { method: 'POST', body: { content } }),
    ),
    restaurar: aba((version: number) =>
      request<DoctorAutopilot>(`${base}/memoria/restaurar`, { method: 'POST', body: { version } }),
    ),
    importar: aba((_: void) =>
      request<DoctorAutopilot>(`${base}/memoria/importar-respostas`, { method: 'POST' }),
    ),
    novoTeste: aba(
      (t: { question: string; expected: string; expectAction: 'responder' | 'escalar' }) =>
        request<DoctorAutopilot>(`${base}/testes`, { method: 'POST', body: t }),
    ),
    apagarTeste: aba((id: Uuid) =>
      request<DoctorAutopilot>(`${base}/testes/${id}`, { method: 'DELETE' }),
    ),
    rodarTestes: aba((_: void) =>
      request<DoctorAutopilot>(`${base}/testes/rodar`, { method: 'POST' }),
    ),
    estilo: useMutation({
      mutationFn: () =>
        request<MemoryContent['estilo']>(`${base}/estilo/rascunho`, { method: 'POST' }),
      meta: ERRO_NA_TELA,
    }),
    simular: useMutation({
      mutationFn: (v: { turnos: SimulatorTurn[]; oferecidos: readonly string[] }) =>
        request<SimulatorResult>(`${base}/simular`, { method: 'POST', body: v }),
      meta: ERRO_NA_TELA,
    }),
  };
}

export function useAprendizado() {
  const client = useQueryClient();
  const invalidar = () => {
    void client.invalidateQueries({ queryKey: ['piloto'] });
    void client.invalidateQueries({ queryKey: ['conversation'] });
  };
  return {
    aprovar: useMutation({
      mutationFn: (v: { id: Uuid; question: string; answer: string }) =>
        request<{ ok: true }>(`/api/piloto/aprendizados/${v.id}/aprovar`, {
          method: 'POST',
          body: { question: v.question, answer: v.answer },
        }),
      meta: ERRO_NA_TELA,
      onSuccess: invalidar,
    }),
    descartar: useMutation({
      mutationFn: (id: Uuid) =>
        request<{ ok: true }>(`/api/piloto/aprendizados/${id}/descartar`, { method: 'POST' }),
      onSuccess: invalidar,
    }),
  };
}

/** Tirar do piloto, devolver, "Respondo eu" e descartar a sugestão (plano §6.3). */
export function useAcoesDoPilotoNaConversa(conversationId: Uuid) {
  const client = useQueryClient();
  const acao = (caminho: string) =>
    useMutation({
      mutationFn: () =>
        request<{ ok: true }>(`/api/conversations/${conversationId}/piloto/${caminho}`, {
          method: 'POST',
        }),
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: keys.conversation(conversationId) });
        void client.invalidateQueries({ queryKey: keys.conversations });
        void client.invalidateQueries({ queryKey: ['piloto'] });
      },
    });
  return {
    tirar: acao('assumir'),
    devolver: acao('devolver'),
    respondoEu: acao('respondo-eu'),
    descartarSugestao: acao('rascunho/descartar'),
    liberarPendente: acao('pendente/liberar'),
  };
}

export interface MarcaSemPiloto {
  readonly noAutopilot: boolean;
  readonly since: string | null;
  readonly reason: string | null;
}

export function useSemPiloto(contactId: Uuid) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ativo: boolean) =>
      request<MarcaSemPiloto>(`/api/contacts/${contactId}/sem-piloto`, {
        method: 'POST',
        body: { ativo },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.conversations });
      void client.invalidateQueries({ queryKey: ['conversation'] });
    },
  });
}

/** "1:12": quanto falta para o piloto responder. */
export function contagem(ate: string, agora: number): string {
  const s = Math.max(0, Math.round((new Date(ate).getTime() - agora) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
